import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import type { ProjectContentTableOfContentTab } from '../types';
import type { ItemsProps } from '../types';
import { gallery, highlights, investors, partners, subPartners, teamMembers } from './constants';

export interface SubTitleProps extends ComponentPropsWithoutRef<'h3'> {
  children: ReactNode;
}

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description: string;
}

const SubTitle = ({ children, className }: SubTitleProps) => {
  return (
    <h3
      className={classNames('text-2xl font-semibold text-neutral-500 max-sm:text-base', className)}
    >
      {children}
    </h3>
  );
};

const List = ({ items, description, className }: ListProps) => {
  return (
    <>
      <p className="mb-4 text-2xl max-sm:text-base font-semibold text-neutral-500">{description}</p>
      <ul className={classNames('list-disc pl-6', className)}>
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id}>
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction and Highlights',
    content: (
      <>
        <SubTitle className="mb-4">Introduction</SubTitle>
        <p className="mb-6 max-sm:text-sm">
          AutoLayer is the multi-chain Liquid Restaking Tokens (LRT) Finance app. Restake into
          various LRT options with just one click while conserving and compounding your yields
          effortlessly. Furthermore, AutoLayer serves as the gateway to LRTfi, giving users the
          power to create strategies to unleash the potential of LRTs.
        </p>
        <List items={highlights} description="Key Highlights:" />
      </>
    ),
  },
  {
    id: 'product',
    title: 'Product',
    content: (
      <>
        <p className="mb-6 max-sm:text-sm">
          AutoLayer is the multi-chain hub for Liquid Restaking, Yields, & LRT Airdrops. Users can
          harness the power of Ethereum by leveraging multiple LRT/LST options with just one click
          while conserving and compounding their yields effortlessly.
        </p>
        <p className="mb-6 max-sm:text-sm">
          AutoLayer hosts a full suite of LRTs, LSTs, and other 1-click products that users can
          deposit to. Trades in AutoLayer are rewarded with Points from both AutoLayer and the
          protocol, which can lead to even bigger rewards in the form of airdrops and other
          incentives.
        </p>
        <SubTitle className="mb-4">Problem</SubTitle>
        <p className="mb-6 max-sm:text-sm">
          LRTfi and restaking protocol ecosystems like EigenLayer and Symbiotic are among the most
          exciting topics in this cycle, but they come with some risks. LRTs are derivatives created
          on top of the security of Ethereum, also providing security to AVSs. This generates new
          opportunities and new risks.
        </p>
        <SubTitle className="mb-4">Solution</SubTitle>
        <p className="mb-6 max-sm:text-sm">
          AutoLayer is a simple interface that lets you trade, analyze, and make the best of the
          LRTfi ecosystem, enabling users to make better decisions and act swiftly. AutoLayer also
          automates complex DeFi operations, making the management of these products easy,
          productive, and safe.
        </p>
        <div className="overflow-hidden -mb-6">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt=""
                  width={1920}
                  height={1080}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model and Revenue Streams',
    content: (
      <>
        <p className="max-sm:text-sm font-semibold">
          {`AutoLayer business model employs a combination of a tokenomics strategy, liquidity
          provision incentives, decentralized finance (DeFi) principles, and innovation in product
          offerings to sustain and grow its operations. Here's a detailed breakdown of the model:`}
        </p>
        <h1 className="my-4 font-semibold">1. Token Utility & Ecosystem Expansion</h1>
        <p className="font-normal">
          <span className="font-semibold">AutoLayer Token (LAY3R) Growth: </span>The company
          utilizes the AutoLayer token (LAY3R) as the cornerstone of its ecosystem. The token plays
          a critical role in the protocol, being essential for liquidity provisioning, staking,
          governance, and access to premium services within the ecosystem. As user adoption
          increases, demand for the token rises, naturally leading to potential appreciation in its
          value due to scarcity and utility.
        </p>
        <p className="font-normal">
          <span className="font-semibold">No Initial Fees: </span>In the early stages, the platform
          imposes no transaction fees, which incentivizes adoption by reducing the friction for new
          users. This helps the platform rapidly attract liquidity providers and users, creating
          network effects that contribute to increased engagement and platform momentum.
        </p>

        <h1 className="my-4 font-semibold">2. Introduction of Transaction Fees</h1>
        <p className="font-normal">
          <span className="font-semibold">Gradual Implementation of Fees: </span>As the user base
          matures, a transaction fee structure will be gradually introduced, ranging from 0.05% to
          0.20% on all transactions. This serves as a key revenue stream for the company. However,
          rather than extracting this value, the fees are reinvested back into the ecosystem to
          ensure its sustainability and long-term growth.
        </p>
        <p className="font-normal">
          <span className="font-semibold">Reinvestment in Liquidity: </span>A significant portion of
          the transaction fees is used to purchase LAY3R and ETH, which are then reinvested into the
          liquidity pool for the LAY3R/ETH trading pair on decentralized exchanges. This reinforces
          liquidity, decentralizes control, and stabilizes the token’s price over time. By directly
          tying fees to liquidity provisioning, the protocol can enhance both security and token
          value stability.
        </p>
        <p className="font-normal">
          <span className="font-semibold">Treasury Revenue Stream: </span>
          {`To ensure the protocol's long-term sustainability, AutoLayer introduces an autonomous
          treasury management system. A portion of the transaction fees (e.g., 10-20%) can be also
          automatically directed to the protocol's treasury.`}
        </p>

        <h1 className="my-4 font-semibold">3. Next-Generation Product Development</h1>
        <p className="font-normal">
          <span className="font-semibold">Next-Gen LRT: </span>AutoLayer is actively working on the
          development of a next-generation LRT. This innovative product will also generate new
          revenue streams.
        </p>
      </>
    ),
  },
  {
    id: 'target-market',
    title: 'Target Market',
    content: (
      <>
        <p>
          All DeFi and LRTs users. AutoLayer aims to amplify the opportunities on restaking
          protocols such as EigenLayer and Symbiotic targeting a broad audience.
        </p>
      </>
    ),
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-6 gap-1.5">
          {investors.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'partners',
    title: 'Partners',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-1.5">
          {partners.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 mt-1.5">
          {subPartners.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <TeamMemberList
          className="!grid-cols-1 !items-start max-md:flex-col"
          cardClassName="!items-start max-md:flex-col"
          teamMembers={teamMembers}
        />
      </>
    ),
  },
  {
    id: 'tokenomics',
    title: 'Tokenomics',
    content: (
      <>
        <ImageProxy
          alt="Tokenomics diagram"
          src="/launchpad/stories/autolayer/auto-layer-tokenomics.png"
          className="block w-full h-auto rounded-lg object-contain shadow-main"
          quality={80}
          width={773}
          height={264}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink
            className="underline"
            href="https://docs.autolayer.io/autolayer/welcome/introduction/tortle-slice-and-recipe-tokens"
          >
            Tokenomics
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: (
      <>
        <p className="mb-2 max-sm:text-sm">
          {`The AutoLayer token is central to the entire Dapp, gaining value as the user base grows
          and rewarding its holders. A platform fee of 0.05% - 0.20% applies to every operation on
          AutoLayer. This fee does not consume gas as it is bundled with the initial transactions.
          The protocol will buy LAY3R and ETH on the market and provide liquidity to the LAY3R/ETH
          pair. A 30-day escrowed version of these LP tokens is sent to the user's wallet, and
          holding those LP tokens will earn more LAY3R tokens over time. The longer the user holds
          them, the greater the yield. Rewards can be restaked into more LPs, growing the user's LP
          position over time.`}
        </p>
      </>
    ),
  },
  {
    id: 'token-deflation',
    title: 'Token Deflation',
    content: (
      <>
        <p className="mb-3 max-sm:text-sm">
          With every transaction, 0.025% to 0.10% of the volume generates buying pressure on a DEX,
          embedded in the transaction without incurring any gas costs. Simultaneously, liquidity in
          that pool increases, making the price more stable over time. As the protocol grows in
          volume, it also enhances deflation and decentralisation of liquidity. By rewarding good
          liquidity providers/holders, the protocol creates a virtuous circle that tackles inflation
          and shares revenues within the community.
        </p>
      </>
    ),
  },
];
